// Der einzige Weg, auf dem ein Geldbetrag auf den Bildschirm kommt (parts4/00-contract.md).
// Nichts formatiert Geld von Hand — auch keine Überschrift, auch keine Zusammenfassung.
import type { ReactElement } from 'react'
import { eur } from '../format'
import './Betrag.css'

export interface BetragProps {
  /** Ganzzahlige Cent, wie die API sie liefert. */
  cents: number
  /** Für Spalten, deren Überschrift das Währungszeichen schon trägt. */
  ohneWaehrung?: boolean
  /** Zusätzliche Layout-Klassen der aufrufenden Zeile. */
  className?: string
}

/**
 * Tabellenziffern, rechtsbündig, Euro in voller Stärke, Nachkommastelle in --ink-muted, negativ in
 * --alert mit echtem Minus (U+2212).
 *
 * Das aria-label trägt die gesprochene Fassung, weil ein Bildschirmleser die aufgeteilte Zahl sonst
 * als zwei Zahlen vorliest ("vier Punkt neun acht null" und "Komma null null"). Die sichtbaren Teile
 * sind deshalb aria-hidden: sie sind Typografie, nicht Inhalt.
 */
export function Betrag({ cents, ohneWaehrung = false, className }: BetragProps): ReactElement {
  const text = eur(cents)
  const negativ = cents < 0
  const ohneVorzeichen = negativ ? text.slice(1) : text
  const komma = ohneVorzeichen.lastIndexOf(',')
  const euros = `${negativ ? '−' : ''}${ohneVorzeichen.slice(0, komma)}`
  const nachkomma = ohneVorzeichen.slice(komma)
  const gesprochen = `${negativ ? 'minus ' : ''}${ohneVorzeichen} Euro`

  return (
    <span
      className={className === undefined ? 'betrag' : `betrag ${className}`}
      data-negativ={negativ ? 'true' : undefined}
      aria-label={gesprochen}
    >
      <span aria-hidden="true" data-teil="euro">
        {euros}
      </span>
      <span aria-hidden="true" data-teil="nachkomma" className="betrag-nachkomma">
        {nachkomma}
      </span>
      {!ohneWaehrung && (
        <span aria-hidden="true" data-teil="waehrung" className="betrag-waehrung">
          {' €'}
        </span>
      )}
    </span>
  )
}
